/**
 * Customer portal for law offices and courts: their papers, status, attempts and who has them;
 * Recall / Message / Add note; "Notify me" choices; Print affidavit once served / non-served and
 * notarized.
 */
import { PageHeader } from '../shared/ui.tsx';
import { useEffect, useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Empty, ErrorBox, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { SendPapers } from './SendPapers.tsx';

type View = 'all' | 'open' | 'served' | 'not_served' | 'recalled';
const VIEWS: [View, string][] = [
  ['all', 'All papers'],
  ['open', 'In progress'],
  ['served', 'Served'],
  ['not_served', 'Not served'],
  ['recalled', 'Recalled'],
];
const EVENTS: [string, string][] = [
  ['assigned', 'Assigned to a deputy'],
  ['attempted', 'Each attempt'],
  ['served', 'Served'],
  ['non_served', 'Not served (non est)'],
  ['affidavit_ready', 'Affidavit ready to print'],
  ['recalled', 'Recalled'],
  ['message', 'Messages from the sheriff’s office'],
];
const d = (x: string) => new Date(x).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
const dt = (x: string) => new Date(x).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });

type Dialog = { kind: 'recall' | 'message' | 'note'; paper: any } | { kind: 'notify' } | null;

export function PortalApp({ embedded = false }: { embedded?: boolean } = {}) {
  const { me, logout } = useAuth();
  const [view, setView] = useState<View>('all');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [bell, setBell] = useState(false);
  const [bump, setBump] = useState(0);
  const [sending, setSending] = useState(false);
  const list = useLoad(() => api.get<any>(`/api/portal/papers?view=${view}&q=${encodeURIComponent(search)}`), [view, search]);
  const notes = useLoad(() => api.get<any>('/api/portal/notifications'), []);
  useEffect(() => {
    const t = setInterval(() => notes.reload(), 60_000);
    return () => clearInterval(t);
  }, []);

  if (embedded)
    return (
      <div className="ops stack portal-embedded">
        <PageHeader crumb="Court" title="Civil papers" sub={`Papers from ${list.data?.customer ?? 'your court'} and where each one stands.`} actions={<button className="btn primary" onClick={() => setSending(true)}>Send papers</button>} />
      <main className="portal-main">
        {sending ? (
          <SendPapers onBack={() => (setSending(false), list.reload())} />
        ) : (
        <>
        <div className="row between wrap gap">
          <div className="tabs">
            {VIEWS.map(([k, l]) => (
              <button key={k} className={view === k ? 'active' : ''} onClick={() => setView(k)}>
                {l}
              </button>
            ))}
          </div>
          <form
            className="row gap"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(q.trim());
            }}
          >
            <input aria-label="Search papers" placeholder="Case number, defendant or paper number" value={q} onChange={(e) => setQ(e.target.value)} className="portal-q" />
            <button className="btn">Search</button>
          </form>
        </div>
        <ErrorBox error={list.error} />
        {!list.data ? (
          <Spinner />
        ) : list.data.papers.length === 0 ? (
          <Empty>No papers here.</Empty>
        ) : (
          <div className="table-wrap portal-table-wrap">
            <table className="table portal-table">
              <thead>
                <tr>
                  <th>Paper / case</th>
                  <th>Defendant</th>
                  <th>Status</th>
                  <th>Assigned to</th>
                  <th>Attempts</th>
                  <th className="actions-h">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.papers.map((p: any) => (
                  <PaperRow key={p.id} p={p} open={open === p.id} bump={bump} onToggle={() => setOpen(open === p.id ? null : p.id)} onDialog={(kind) => setDialog({ kind, paper: p })} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </main>
      {dialog?.kind === 'notify' && <NotifyDialog onClose={() => (setDialog(null), notes.reload())} />}
      {dialog && dialog.kind !== 'notify' && (
        <PostDialog
          kind={dialog.kind}
          paper={dialog.paper}
          onClose={(changed) => {
            setDialog(null);
            if (changed) {
              list.reload();
              setOpen(dialog.paper.id);
              setBump((n) => n + 1);
            }
          }}
        />
      )}
      </div>
    );
  return (
    <div className="portal">
      <header className="portal-top">
        <div className="portal-brand">
          <img src="/civilhound-icon-clear.webp" alt="" width={34} height={34} />
          <div>
            <div className="portal-agency">{me!.agency.name}</div>
            <div className="portal-sub">Customer portal{list.data ? ` · ${list.data.customer}` : ''}</div>
          </div>
        </div>
        <span className="spacer" />
        <button className="btn sm primary portal-send" onClick={() => setSending(true)}>
          ⇪ Send papers
        </button>
        <button className="btn sm portal-hbtn" onClick={() => setDialog({ kind: 'notify' })}>
          Notify me…
        </button>
        <button
          className="btn sm portal-hbtn bell"
          aria-label={`Notifications${notes.data?.unread ? `, ${notes.data.unread} new` : ''}`}
          onClick={() => {
            setBell(!bell);
            if (!bell && notes.data?.unread) api.post('/api/portal/notifications/seen').catch(() => undefined);
          }}
        >
          🔔 {notes.data?.unread ? <span className="bell-n">{notes.data.unread}</span> : null}
        </button>
        <span className="portal-who">{me!.user.displayName}</span>
        <button className="btn sm ghost portal-hbtn" onClick={logout}>
          Sign out
        </button>
      </header>
      {bell && (
        <div className="bell-panel" role="dialog" aria-label="Notifications">
          <div className="row between">
            <strong>Notifications</strong>
            <button className="btn sm ghost" onClick={() => (setBell(false), notes.reload())}>
              Close
            </button>
          </div>
          {!notes.data ? (
            <Spinner />
          ) : notes.data.items.length === 0 ? (
            <Empty>Nothing yet. Choose what you want to hear about under “Notify me”.</Empty>
          ) : (
            <ul className="plain bell-list">
              {notes.data.items.map((n: any, i: number) => (
                <li key={i} className={n.unread ? 'unread' : ''}>
                  <button
                    className="linkish"
                    onClick={() => {
                      setOpen(n.paperId);
                      setBell(false);
                    }}
                  >
                    <span className="mono">{n.paperNumber}</span> {n.defendant}
                  </button>
                  <div>{n.text}</div>
                  <div className="small muted">{n.when}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <main className="portal-main">
        {sending ? (
          <SendPapers onBack={() => (setSending(false), list.reload())} />
        ) : (
        <>
        <div className="row between wrap gap">
          <div className="tabs">
            {VIEWS.map(([k, l]) => (
              <button key={k} className={view === k ? 'active' : ''} onClick={() => setView(k)}>
                {l}
              </button>
            ))}
          </div>
          <form
            className="row gap"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(q.trim());
            }}
          >
            <input aria-label="Search papers" placeholder="Case number, defendant or paper number" value={q} onChange={(e) => setQ(e.target.value)} className="portal-q" />
            <button className="btn">Search</button>
          </form>
        </div>
        <ErrorBox error={list.error} />
        {!list.data ? (
          <Spinner />
        ) : list.data.papers.length === 0 ? (
          <Empty>No papers here.</Empty>
        ) : (
          <div className="table-wrap portal-table-wrap">
            <table className="table portal-table">
              <thead>
                <tr>
                  <th>Paper / case</th>
                  <th>Defendant</th>
                  <th>Status</th>
                  <th>Assigned to</th>
                  <th>Attempts</th>
                  <th className="actions-h">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.papers.map((p: any) => (
                  <PaperRow key={p.id} p={p} open={open === p.id} bump={bump} onToggle={() => setOpen(open === p.id ? null : p.id)} onDialog={(kind) => setDialog({ kind, paper: p })} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </main>
      {dialog?.kind === 'notify' && <NotifyDialog onClose={() => (setDialog(null), notes.reload())} />}
      {dialog && dialog.kind !== 'notify' && (
        <PostDialog
          kind={dialog.kind}
          paper={dialog.paper}
          onClose={(changed) => {
            setDialog(null);
            if (changed) {
              list.reload();
              setOpen(dialog.paper.id);
              setBump((n) => n + 1);
            }
          }}
        />
      )}
      <footer className="portal-foot">Questions about a paper? Use Message on that paper, or call {me!.agency.name}.</footer>
    </div>
  );
}

function PaperRow({ p, open, bump, onToggle, onDialog }: { p: any; open: boolean; bump: number; onToggle: () => void; onDialog: (k: 'recall' | 'message' | 'note') => void }) {
  const detail = useLoad(() => (open ? api.get<any>(`/api/portal/papers/${p.id}`) : Promise.resolve(null)), [open, p.unreadMessages, p.status, bump]);
  const tone = p.recalled ? 'neutral' : /Served/.test(p.status) || p.statusCode === 'SERVED' ? 'ok' : p.statusCode === 'NON_SERVED' ? 'bad' : 'info';
  return (
    <>
      <tr className={`portal-row ${open ? 'open' : ''}`}>
        <td>
          <button className="linkish mono" onClick={onToggle} aria-expanded={open}>
            {p.paperNumber}
          </button>
          <div className="small muted">
            {p.caseNumber} · {p.paperType}
          </div>
        </td>
        <td>
          <strong>{p.defendant}</strong>
          <div className="small muted">{p.address}</div>
        </td>
        <td>
          <span className={`badge ${tone}`}>{p.status}</span>
          {p.servedAt && <div className="small muted">{dt(p.servedAt)}</div>}
          {p.unreadMessages > 0 && <div className="small new-msg">{p.unreadMessages} new message{p.unreadMessages === 1 ? '' : 's'}</div>}
        </td>
        <td>{p.assignedTo ?? <span className="muted">Not yet</span>}</td>
        <td>
          {p.attempts}
          {p.lastAttempt && (
            <div className="small muted">
              Last: {p.lastAttempt.result}, {dt(p.lastAttempt.at)}
            </div>
          )}
        </td>
        <td className="portal-actions">
          <button className="btn sm" onClick={() => onDialog('recall')} disabled={!p.canRecall} title={p.canRecall ? 'Stop service of this paper' : p.recalled ? 'Already recalled' : 'Already complete'}>
            Recall
          </button>
          <button className="btn sm" onClick={() => onDialog('message')}>
            Message
          </button>
          <button className="btn sm" onClick={() => onDialog('note')}>
            Add note
          </button>
          {p.affidavitReady ? (
            <a className="btn sm primary" href={`/api/portal/papers/${p.id}/affidavit.pdf`} target="_blank" rel="noreferrer">
              Print affidavit
            </a>
          ) : (
            <button className="btn sm" disabled title="Available once the paper is served or not served and the affidavit is notarized">
              Print affidavit
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="portal-detail">
          <td colSpan={6}>
            {!detail.data ? (
              <Spinner />
            ) : (
              <div className="portal-detail-grid">
                <div>
                  <h3>Attempts</h3>
                  {detail.data.attempts.length === 0 ? (
                    <p className="muted">No attempts yet.</p>
                  ) : (
                    <ol className="portal-attempts">
                      {detail.data.attempts.map((a: any, i: number) => (
                        <li key={i}>
                          <strong>{dt(a.at)}</strong> · {a.result}
                          {a.method ? ` (${a.method})` : ''} · {a.deputy}
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="small muted">
                    Received {d(p.receivedAt)}
                    {p.courtDate ? ` · Court date ${d(p.courtDate)}` : ''}
                    {p.plaintiff ? ` · ${p.plaintiff}` : ''}
                  </p>
                </div>
                <div>
                  <h3>Messages &amp; notes</h3>
                  {detail.data.posts.length === 0 ? (
                    <p className="muted">None yet.</p>
                  ) : (
                    <ul className="plain portal-thread">
                      {detail.data.posts.map((m: any) => (
                        <li key={m.id} className={`${m.from_side} ${m.kind}`}>
                          <div className="small muted">
                            {m.kind === 'note' ? 'Note' : 'Message'} · {m.from_side === 'office' ? `Sheriff’s office (${m.author})` : m.author} · {dt(m.created_at)}
                          </div>
                          <div>{m.body}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function PostDialog({ kind, paper, onClose }: { kind: 'recall' | 'message' | 'note'; paper: any; onClose: (changed: boolean) => void }) {
  const [text, setText] = useState('');
  const a = useAction();
  const cfg = {
    recall: { title: `Recall ${paper.paperNumber}`, label: 'Reason for the recall', button: 'Recall this paper', hint: 'The deputy will stop working this paper. This can’t be undone from the portal.' },
    message: { title: `Message about ${paper.paperNumber}`, label: 'Message to the sheriff’s office', button: 'Send message', hint: 'The civil process office will reply here.' },
    note: { title: `Add a note to ${paper.paperNumber}`, label: 'Note', button: 'Add note', hint: 'Notes go to the office and the deputy serving the paper: gate codes, work hours, a better address.' },
  }[kind];
  const submit = () =>
    a.run(async () => {
      if (kind === 'recall') await api.post(`/api/portal/papers/${paper.id}/recall`, { reason: text.trim() });
      else await api.post(`/api/portal/papers/${paper.id}/${kind === 'message' ? 'messages' : 'notes'}`, { body: text.trim() });
      onClose(true);
    });
  return (
    <div className="modal-back" role="dialog" aria-label={cfg.title}>
      <form
        className="modal"
        data-unsaved={text ? 'true' : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h2>{cfg.title}</h2>
        <p className="small muted">
          {paper.defendant} · {paper.caseNumber}
        </p>
        <label className="field">
          <span className="field-l">{cfg.label}</span>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} autoFocus required />
        </label>
        <p className="small muted">{cfg.hint}</p>
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className={`btn ${kind === 'recall' ? 'danger' : 'primary'}`} disabled={a.busy || !text.trim()}>
            {cfg.button}
          </button>
          <button type="button" className="btn ghost" onClick={() => onClose(false)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function NotifyDialog({ onClose }: { onClose: () => void }) {
  const r = useLoad(() => api.get<any>('/api/portal/notify'), []);
  const [events, setEvents] = useState<string[] | null>(null);
  const [email, setEmail] = useState<boolean | null>(null);
  const a = useAction();
  if (!r.data) return null;
  const ev = events ?? r.data.events;
  const em = email ?? r.data.email;
  const all = EVENTS.every(([k]) => ev.includes(k));
  const toggle = (k: string) => setEvents(ev.includes(k) ? ev.filter((x: string) => x !== k) : [...ev, k]);
  return (
    <div className="modal-back" role="dialog" aria-label="Notify me">
      <form
        className="modal"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            await api.post('/api/portal/notify', { events: ev, email: em });
            onClose();
          });
        }}
      >
        <h2>Notify me when…</h2>
        <label className="notify-all">
          <input type="checkbox" checked={all} onChange={() => setEvents(all ? [] : EVENTS.map(([k]) => k))} /> <strong>All status changes</strong>
        </label>
        <div className="notify-list">
          {EVENTS.map(([k, l]) => (
            <label key={k}>
              <input type="checkbox" checked={ev.includes(k)} onChange={() => toggle(k)} /> {l}
            </label>
          ))}
        </div>
        <hr />
        <label className={r.data.emailAvailable ? '' : 'muted'}>
          <input type="checkbox" checked={em} disabled={!r.data.emailAvailable} onChange={(e) => setEmail(e.target.checked)} /> Also email me{r.data.emailAddress ? ` at ${r.data.emailAddress}` : ''}
        </label>
        {!r.data.emailAvailable && <p className="small muted">Email alerts start once the sheriff’s office turns them on. Until then, alerts show under the bell here.</p>}
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            Save
          </button>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
